import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import PricingTiersSection from './PricingTiersSection';
import CorporateCodesSection from './CorporateCodesSection';

/*
 * A port of the Scale portal's Account -> Pricing tab
 * (scale-portal/src/components/template/Account/Tabs/PricingTab.jsx) for the
 * Case Manager portal: same sections, same copy, same field visibility, same
 * currency mask, and the same per-field auto-save with no Save button.
 *
 * Save triggers match Scale field for field:
 *   Billing Type, Rate Display  -> on change
 *   Hourly Rate, Deposit, Half-day, Full-day, GSC rate, Admin Fee Label -> on blur
 *   Processing Fee -> on blur (onChange only updates local state, as in Scale)
 *
 * One deliberate difference: a failed save shows an error. Scale catches it and
 * logs to the console, so the field keeps the typed value and nobody is told.
 */

const ADMIN_FEE_LABEL_DEFAULT = 'Scale Mediation Admin Fee';

// Scale's mask, verbatim (MediationRates.jsx:9-21): display as $1,200, integers
// only, state holds a bare digit string.
const formatDollarAmount = (value) => {
  if (typeof value !== 'string') value = `${value}`;
  if (!value) return '';
  const numberValue = parseInt(value.replace(/[^0-9]/g, ''), 10);
  if (isNaN(numberValue)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0,
  }).format(numberValue);
};
// NOTE the difference from Scale's toNumber, which returns 0 for a blank.
// Clearing a rate there writes a real $0, and $0 is a legitimate rate — so the
// mediator's next fixed-rate case quotes and invoices zero with nothing to
// distinguish it from a deliberate free session. Blank means "not set" here,
// which is also what the backend was built to receive.
const toNumberOrNull = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const n = parseInt(String(value), 10);
  return isNaN(n) ? null : n;
};

const BILLING_TYPE_OPTIONS = [
  { label: 'Hourly Rate', value: 'hourly' },
  { label: 'Fixed Rate', value: 'fixed' },
];
const RATE_DISPLAY_OPTIONS = [
  { label: 'Total Rate', value: 'total' },
  { label: 'Per Party', value: 'per_party' },
];
const PROCESSING_FEE_OPTIONS = [
  { label: 'Pay By Client', value: 'true' },
  { label: 'Pay By Mediator', value: 'false' },
];

function SectionHeading({ title, subtitle }) {
  return (
    <div className="flex flex-col gap-y-1 py-2">
      <h3 className="text-xl font-semibold text-[#0e1e20]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>{title}</h3>
      {subtitle && (
        <p className="text-sm font-normal text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>{subtitle}</p>
      )}
    </div>
  );
}

// Scale's own control classes, ported into src/styles/index.css.
const inputClass = 'input input-md';
const selectClass = 'select-control';
const controlStyle = { fontFamily: 'Inter, sans-serif', color: '#0e1e20' };

function Labelled({ label, children }) {
  return (
    <div className="col-span-2 md:col-span-1">
      <label className="text-black pb-2 flex items-center text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{label}</label>
      {children}
    </div>
  );
}

export default function PricingTab({ mediatorId, notify, onSaved, userId }) {
  const [values, setValues] = useState({
    pricing_type: '', hourly_rate: '', deposit: '', halfday: '', fullday: '',
    processing_fee_to_client: true, hourly_rate_display_mode: 'total',
    gsc_hourly_rate: '', invoice_admin_fee_label: '',
    // Written by the two radio "tabs" in the party-count section, as in Scale.
    invoice_allocation_party: 'equally_split',
  });
  const [context, setContext] = useState({ isGsc: false, gscAdminFee: null, isOdr: false });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!mediatorId) { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await api.get(`/mediators/${mediatorId}/pricing`);
      const d = res.data?.data || {};
      setValues({
        pricing_type: d.pricing_type || '',
        hourly_rate: d.hourly_rate == null ? '' : String(d.hourly_rate),
        deposit: d.deposit == null ? '' : String(d.deposit),
        halfday: d.half_day_rate == null ? '' : String(d.half_day_rate),
        fullday: d.full_day_rate == null ? '' : String(d.full_day_rate),
        processing_fee_to_client: d.processing_fee_to_client !== false,
        hourly_rate_display_mode: d.hourly_rate_display_mode || 'total',
        gsc_hourly_rate: d.gsc_hourly_rate == null ? '' : String(d.gsc_hourly_rate),
        invoice_admin_fee_label: d.invoice_admin_fee_label || '',
        invoice_allocation_party: d.invoice_allocation_party || 'equally_split',
      });
      setContext({
        isGsc: d.is_gsc_cases_allowed === true,
        gscAdminFee: d.gsc_admin_fee ?? null,
        isOdr: d.is_odr_mediator === true,
      });
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to load pricing' });
    } finally {
      setLoading(false);
    }
  }, [mediatorId, notify]);

  useEffect(() => { load(); }, [load]);

  // One save path for every field, as Scale has one handleUpdateField.
  const save = async (payload) => {
    try {
      await api.put(`/mediators/${mediatorId}/pricing`, payload);
      notify?.({ type: 'success', message: 'Changes Saved' });
      onSaved?.();
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to save' });
      load();
    }
  };

  const setRaw = (key, e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setValues((p) => ({ ...p, [key]: raw }));
  };
  const set = (key, v) => setValues((p) => ({ ...p, [key]: v }));

  if (loading) {
    return (
      <div className="flex items-center justify-center bg-white rounded-md shadow" style={{ padding: '48px 0' }}>
        <div className="w-6 h-6 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isHourly = values.pricing_type === 'hourly';
  const isFixed = values.pricing_type === 'fixed';

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* ---------------- Rates ---------------- */}
      <div className="col-span-2">
        <SectionHeading title="Rates" subtitle="Set your rates for services" />
        <div className="flex flex-col gap-y-10 bg-white p-2 lg:p-8 rounded-md shadow">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <Labelled label="Billing Type">
              <select
                className={selectClass}
                style={controlStyle}
                value={values.pricing_type}
                onChange={(e) => { set('pricing_type', e.target.value); save({ pricing_type: e.target.value }); }}
              >
                <option value="">Select</option>
                {BILLING_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Labelled>

            {isHourly && (
              <>
                <Labelled label="Rate Display">
                  <select
                    className={inputClass}
                    style={controlStyle}
                    value={values.hourly_rate_display_mode}
                    onChange={(e) => { set('hourly_rate_display_mode', e.target.value); save({ hourly_rate_display_mode: e.target.value }); }}
                  >
                    {RATE_DISPLAY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Labelled>
                <Labelled label="Hourly Rate">
                  <input
                    className={inputClass} style={controlStyle} placeholder="Enter hourly rate"
                    value={formatDollarAmount(values.hourly_rate)}
                    onChange={(e) => setRaw('hourly_rate', e)}
                    onBlur={() => save({ hourly_rate: toNumberOrNull(values.hourly_rate) })}
                  />
                </Labelled>
                <Labelled label="Deposit">
                  <input
                    className={inputClass} style={controlStyle} placeholder="Enter deposit"
                    value={formatDollarAmount(values.deposit || 0)}
                    onChange={(e) => setRaw('deposit', e)}
                    onBlur={() => save({ deposit: toNumberOrNull(values.deposit) })}
                  />
                </Labelled>
              </>
            )}

            {isFixed && (
              <>
                <Labelled label="Half-day">
                  <input
                    className={inputClass} style={controlStyle}
                    placeholder={context.isOdr ? 'Enter hour rate' : 'Enter half-day'}
                    value={formatDollarAmount(values.halfday)}
                    onChange={(e) => setRaw('halfday', e)}
                    onBlur={() => save({ half_day_rate: toNumberOrNull(values.halfday) })}
                  />
                </Labelled>
                <Labelled label="Full-day">
                  <input
                    className={inputClass} style={controlStyle} placeholder="Enter full-day"
                    value={formatDollarAmount(values.fullday)}
                    onChange={(e) => setRaw('fullday', e)}
                    onBlur={() => save({ full_day_rate: toNumberOrNull(values.fullday) })}
                  />
                </Labelled>
              </>
            )}

            {/* Always last, as in Scale. */}
            <Labelled label="Processing Fee">
              <select
                className={selectClass}
                style={controlStyle}
                value={values.processing_fee_to_client ? 'true' : 'false'}
                onChange={(e) => set('processing_fee_to_client', e.target.value === 'true')}
                onBlur={() => save({ processing_fee_to_client: values.processing_fee_to_client })}
              >
                {PROCESSING_FEE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Labelled>
          </div>
        </div>
      </div>

      {/* ---------------- GSC ---------------- */}
      {context.isGsc && (
        <div className="col-span-2">
          <SectionHeading title="GSC Mediation Rates" subtitle="Set your GSC hourly rate" />
          <div className="flex flex-col gap-y-10 bg-white p-2 lg:p-8 rounded-md shadow">
            <div className="grid grid-cols-4 gap-6">
              <Labelled label="GSC Hourly Rate">
                <input
                  className={inputClass} style={controlStyle} placeholder="Enter GSC hourly rate"
                  value={formatDollarAmount(values.gsc_hourly_rate)}
                  onChange={(e) => setRaw('gsc_hourly_rate', e)}
                  onBlur={() => save({ gsc_hourly_rate: toNumberOrNull(values.gsc_hourly_rate) })}
                />
              </Labelled>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Set Pricing By Party Count ---------------- */}
      <PricingTiersSection
        mediatorId={mediatorId}
        notify={notify}
        allocation={values.invoice_allocation_party}
        onAllocationChange={(v) => {
          set('invoice_allocation_party', v);
          save({ invoice_allocation_party: v });
        }}
      />

      {/* ---------------- Admin Fee Label ---------------- */}
      <div className="col-span-2">
        <SectionHeading
          title="Admin Fee Label"
          subtitle="Choose what the administrative fee is called on the invoices your clients receive."
        />
        <div className="flex flex-col w-full gap-y-10 bg-white p-2 lg:p-8 rounded-md shadow">
          <div className="p-4 rounded-xl">
            <label className="text-black pb-3 flex items-center text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              Admin Fee Line Item
            </label>
            <input
              className={inputClass}
              style={controlStyle}
              placeholder={ADMIN_FEE_LABEL_DEFAULT}
              value={values.invoice_admin_fee_label}
              onChange={(e) => set('invoice_admin_fee_label', e.target.value)}
              onBlur={() => save({ invoice_admin_fee_label: values.invoice_admin_fee_label })}
            />
            <p className="text-xs text-gray-500 mt-1 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
              This name applies to invoices created from here on, so it is best not to change it
              while an invoice is being prepared. Leave it blank to use &ldquo;{ADMIN_FEE_LABEL_DEFAULT}&rdquo;.
            </p>
          </div>
        </div>
      </div>
      {/* ---------------- Corporate Code Management ---------------- */}
      <CorporateCodesSection mediatorId={mediatorId} userId={userId} notify={notify} />
    </div>
  );
}
